<div class="ai-review-shell">
  <!-- Loading & error states -->
  <div class="ai-loading" *ngIf="loading">
    <div class="spinner"></div>
    <p>Loading…</p>
  </div>

  <div class="ai-error" *ngIf="!loading && loadError">
    <p>We couldn't load the review experience. Please try again.</p>
  </div>

  <!-- Main flow -->
  <div class="ai-review-card" *ngIf="!loading && !loadError && config">

    <header class="ai-topbar">
      <div class="business-name">{{ config.business_name }}</div>
      <div class="topbar-sub">Leave a review in a few quick taps</div>
    </header>

    <!-- ===================================================================
         Vertical stage list. Each stage is one row in a track on the left
         + content panel on the right. Active stage is expanded; completed
         stages collapse to a summary line; future stages are locked.
         =================================================================== -->
    <ol class="stage-list">

      <!-- =========================== STAGE 1 =========================== -->
      <li
        class="stage"
        [class.active]="step === 1"
        [class.done]="step > 1"
        [class.locked]="step < 1"
      >
        <button
          type="button"
          class="stage-rail"
          (click)="jumpTo(1)"
          [attr.aria-expanded]="step === 1"
        >
          <span class="rail-node">
            <span class="node-num" *ngIf="step <= 1">1</span>
            <span class="node-check" *ngIf="step > 1">✓</span>
          </span>
          <span class="rail-line" aria-hidden="true"></span>
        </button>

        <div class="stage-body">
          <div class="stage-header">
            <div class="stage-title">{{ stages[0].label }}</div>
            <div class="stage-hint" *ngIf="step === 1">{{ stages[0].hint }}</div>
            <div class="stage-summary" *ngIf="step > 1">
              {{ summaryFor(1) }}
              <span class="edit-link">Edit</span>
            </div>
          </div>

          <div class="stage-content" *ngIf="step === 1" [@expand]>
            <div class="thumbs-row">
              <button
                class="thumb-btn positive"
                [class.selected]="sentiment === 'positive'"
                (click)="selectSentiment('positive')"
                aria-label="Positive"
              >
                <img src="assets/images/thumbs-up.svg" alt="" />
                <span>Good</span>
              </button>

              <button
                class="thumb-btn negative"
                [class.selected]="sentiment === 'negative'"
                (click)="selectSentiment('negative')"
                aria-label="Negative"
              >
                <img src="assets/images/thumbs-down.svg" alt="" />
                <span>Needs Improvement</span>
              </button>
            </div>
          </div>
        </div>
      </li>

      <!-- =========================== STAGE 2 =========================== -->
      <li
        class="stage"
        [class.active]="step === 2"
        [class.done]="step > 2"
        [class.locked]="step < 2"
      >
        <button
          type="button"
          class="stage-rail"
          (click)="jumpTo(2)"
          [attr.aria-expanded]="step === 2"
        >
          <span class="rail-node">
            <span class="node-num" *ngIf="step <= 2">2</span>
            <span class="node-check" *ngIf="step > 2">✓</span>
          </span>
          <span class="rail-line" aria-hidden="true"></span>
        </button>

        <div class="stage-body">
          <div class="stage-header">
            <div class="stage-title">{{ stages[1].label }}</div>
            <div class="stage-hint" *ngIf="step === 2">{{ stages[1].hint }}</div>
            <div class="stage-summary" *ngIf="step > 2">
              {{ summaryFor(2) }}
              <span class="edit-link">Edit</span>
            </div>
          </div>

          <div class="stage-content" *ngIf="step === 2" [@expand]>
            <div class="chip-grid">
              <button
                *ngFor="let kw of config.keywords"
                type="button"
                class="chip"
                [class.selected]="isKeywordSelected(kw)"
                [class.others]="kw === config.others_keyword"
                (click)="toggleKeyword(kw)"
              >
                {{ kw }}
              </button>
            </div>

            <div class="others-input" *ngIf="isOthersSelected">
              <label for="othersText">Tell us briefly:</label>
              <input
                id="othersText"
                type="text"
                maxlength="80"
                placeholder="e.g. parking was easy"
                [(ngModel)]="othersText"
                autocomplete="off"
              />
            </div>

            <button
              class="primary-btn"
              [disabled]="!canProceedFromScreen2"
              (click)="goNext()"
            >
              Continue
            </button>
          </div>
        </div>
      </li>

      <!-- =========================== STAGE 3 =========================== -->
      <li
        class="stage"
        [class.active]="step === 3"
        [class.done]="step > 3"
        [class.locked]="step < 3"
      >
        <button
          type="button"
          class="stage-rail"
          (click)="jumpTo(3)"
          [attr.aria-expanded]="step === 3"
        >
          <span class="rail-node">
            <span class="node-num" *ngIf="step <= 3">3</span>
            <span class="node-check" *ngIf="step > 3">✓</span>
          </span>
          <span class="rail-line" aria-hidden="true"></span>
        </button>

        <div class="stage-body">
          <div class="stage-header">
            <div class="stage-title">{{ stages[2].label }}</div>
            <div class="stage-hint" *ngIf="step === 3">{{ stages[2].hint }} — pick up to 3</div>
            <div class="stage-summary" *ngIf="step > 3">
              {{ summaryFor(3) }}
              <span class="edit-link">Edit</span>
            </div>
          </div>

          <div class="stage-content" *ngIf="step === 3" [@expand]>
            <div class="chip-grid">
              <button
                *ngFor="let w of currentWordPool"
                type="button"
                class="chip word-chip"
                [class.selected]="isWordSelected(w)"
                (click)="toggleWord(w)"
              >
                {{ w }}
              </button>
            </div>

            <button
              class="primary-btn"
              [disabled]="selectedWords.length === 0"
              (click)="goNext()"
            >
              {{ sentiment === 'negative' ? 'Continue' : 'Write My Review' }}
            </button>
          </div>
        </div>
      </li>

      <!-- =========================== STAGE 4 =========================== -->
      <li
        class="stage last"
        [class.active]="step === 4"
        [class.locked]="step < 4"
      >
        <button
          type="button"
          class="stage-rail"
          [disabled]="true"
        >
          <span class="rail-node">
            <span class="node-num">4</span>
          </span>
        </button>

        <div class="stage-body">
          <div class="stage-header">
            <div class="stage-title">{{ stages[3].label }}</div>
            <div class="stage-hint" *ngIf="step === 4">{{ stages[3].hint }}</div>
          </div>

          <div class="stage-content" *ngIf="step === 4 && sentiment === 'negative'" [@expand]>
            <p class="ask-heading">Tell us what went wrong</p>
            <p class="ask-subheading">Your feedback helps us improve.</p>

            <div class="additional-editor">
              <textarea
                rows="6"
                maxlength="600"
                [(ngModel)]="feedbackText"
                placeholder="Share what happened — we'll take it from here."
                autofocus
              ></textarea>
            </div>

            <button
              class="primary-btn post-btn"
              [disabled]="!feedbackText.trim()"
              (click)="submit()"
            >
              Submit Feedback
            </button>
          </div>

          <div class="stage-content" *ngIf="step === 4 && sentiment === 'positive'" [@expand]>
            <div class="additional-banner" *ngIf="!isEditingAdditional">
              <ng-container *ngIf="!additionalText.trim(); else hasAddedTplV">
                <span class="ab-prompt">Would you like to add anything?</span>
                <button
                  type="button"
                  class="ab-action"
                  [disabled]="isGenerating || isRefining"
                  (click)="openAdditionalEditor()"
                >
                  + Add
                </button>
              </ng-container>
              <ng-template #hasAddedTplV>
                <span class="ab-label">Including:</span>
                <span class="ab-text">"{{ additionalText.trim() }}"</span>
                <button
                  type="button"
                  class="ab-action"
                  [disabled]="isGenerating || isRefining"
                  (click)="openAdditionalEditor()"
                >
                  Edit
                </button>
              </ng-template>
            </div>

            <div class="additional-editor" *ngIf="isEditingAdditional">
              <textarea
                rows="2"
                maxlength="200"
                [(ngModel)]="additionalDraft"
                placeholder="A name or detail you want included"
                autofocus
              ></textarea>
              <div class="ae-actions">
                <button type="button" class="link-btn" (click)="cancelAdditionalEditor()">
                  Cancel
                </button>
                <button type="button" class="save-btn" (click)="saveAdditionalEditor()">
                  Save
                </button>
              </div>
            </div>

            <div class="draft-area" [class.dim]="isGenerating || isRefining">
              <textarea
                rows="6"
                maxlength="600"
                [(ngModel)]="reviewDraft"
                [disabled]="isGenerating"
                placeholder="Your review will appear here…"
              ></textarea>

              <div class="draft-overlay" *ngIf="isGenerating">
                <div class="spinner small"></div>
                <span>Writing your review…</span>
              </div>
            </div>

            <div class="refine-row">
              <button
                class="refine-btn"
                [disabled]="isGenerating || isRefining || !reviewDraft"
                (click)="refine('refine')"
              >
                Polish
              </button>
              <button
                class="refine-btn"
                [disabled]="isGenerating || isRefining || !reviewDraft"
                (click)="refine('short')"
              >
                Make Shorter
              </button>
              <button
                class="refine-btn"
                [disabled]="isGenerating || isRefining || !reviewDraft"
                (click)="refine('long')"
              >
                Make Longer
              </button>
              <button
                class="refine-btn regenerate"
                [disabled]="isGenerating || isRefining"
                (click)="generate()"
                title="Try a different draft"
                aria-label="Try again"
              >
                ↻
              </button>
            </div>

            <button
              class="primary-btn post-btn"
              [disabled]="isGenerating || !reviewDraft.trim()"
              (click)="submit()"
            >
              Copy &amp; Post on Google
            </button>
          </div>
        </div>
      </li>
    </ol>

    <!-- Thank-you mockup (negative sentiment only, no API call) -->
    <div class="thankyou-overlay" *ngIf="showThankYouModal" (click)="closeThankYou()">
      <div class="thankyou-dialog" (click)="$event.stopPropagation()">
        <button
          type="button"
          class="close close-icon-modal"
          aria-label="Close"
          (click)="closeThankYou()"
        >
          <span aria-hidden="true">&times;</span>
        </button>
        <div class="col-lg-12 col-md-12 col-sm-12 thank-you-sec" style="width: auto">
          <div class="thank-you-icon-sec">
            <img src="assets/images/thank-you-icon.svg" class="img-responsive" />
          </div>
          <p class="thank-you-heading">Thank you for your feedback</p>
          <div class="thank-you-message-section">
            <p class="thank-you-message">
              We appreciate you taking the time to share your experience with us.
            </p>
            <p *ngIf="config?.google_review_url" class="thank-you-message-redirect">
              Please
              <a [href]="config?.google_review_url" (click)="closeThankYou()" class="hyper-link-text">click here</a>
              to review us.
            </p>
          </div>
          <div class="col-md-12 col-sm-12 text-center">
            <button class="btn btn-submit" (click)="closeThankYou()">GOT IT</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
